import React, { useId, useState } from 'react'

export const ARTWORK_SIZE_PRESETS = [
  {
    key: 'pocket',
    label: 'Pocket',
    longerSideIn: 4,
    description: 'A compact size for left-chest or pocket placement.',
    garment: 'pocket',
  },
  {
    key: 'infant',
    label: 'Infant',
    longerSideIn: 6,
    description: 'A common size for infant bodysuits and toddler shirts.',
    garment: 'infant',
  },
  {
    key: 'youth',
    label: 'Youth',
    longerSideIn: 8,
    description: 'A common full-front size for youth shirts.',
    garment: 'shirt',
  },
  {
    key: 'adult',
    label: 'Adult',
    longerSideIn: 11,
    description: 'A common full-front size for adult shirts.',
    garment: 'shirt',
  },
  {
    key: 'large',
    label: 'Large',
    longerSideIn: 12,
    description: 'A larger full-front size for adult shirts.',
    garment: 'shirt',
  },
  {
    key: 'xl',
    label: 'XL',
    longerSideIn: 13,
    description: 'An oversized full-front size for adult shirts.',
    garment: 'shirt',
  },
]

const PREVIEW_PLACEMENT = {
  pocket: { x: 80, y: 59, width: 12, height: 18 },
  infant: { x: 67, y: 53, width: 26, height: 39 },
  youth: { x: 65, y: 58, width: 30, height: 45 },
  adult: { x: 63, y: 57, width: 34, height: 51 },
  large: { x: 62, y: 56, width: 36, height: 54 },
  xl: { x: 61, y: 55, width: 38, height: 57 },
}

function roundDimension(value) {
  return Number(value.toFixed(2))
}

export function dimensionsForPreset({ image, config, longerSideIn }) {
  const pixelWidth = Number(image?.widthPx)
  const pixelHeight = Number(image?.heightPx)
  const configuredWidth = Number(config?.printWidthIn)
  const configuredHeight = Number(config?.printHeightIn)
  const sourceWidth = pixelWidth > 0 && pixelHeight > 0 ? pixelWidth : configuredWidth
  const sourceHeight = pixelWidth > 0 && pixelHeight > 0 ? pixelHeight : configuredHeight

  if (!(sourceWidth > 0) || !(sourceHeight > 0) || !(longerSideIn > 0)) return null

  if (sourceWidth >= sourceHeight) {
    return {
      printWidthIn: longerSideIn,
      printHeightIn: roundDimension(longerSideIn * sourceHeight / sourceWidth),
    }
  }

  return {
    printWidthIn: roundDimension(longerSideIn * sourceWidth / sourceHeight),
    printHeightIn: longerSideIn,
  }
}

function GarmentPreview({ preset, imageUrl }) {
  const placement = PREVIEW_PLACEMENT[preset.key]
  const infant = preset.garment === 'infant'

  return (
    <svg
      className="guided-size-presets__garment"
      data-testid={`garment-preview-${preset.key}`}
      viewBox="0 0 160 180"
      aria-hidden="true"
    >
      {infant ? (
        <path d="M51 25 35 38l11 20 12-7v39l-9 38 20 15 11-12 11 12 20-15-9-38V51l12 7 11-20-16-13-17 8H68z" />
      ) : (
        <path d="M55 24 27 40l13 28 18-8v91h44V60l18 8 13-28-28-16-14 9H69z" />
      )}
      {imageUrl && (
        <image
          href={imageUrl}
          x={placement.x}
          y={placement.y}
          width={placement.width}
          height={placement.height}
          preserveAspectRatio="xMidYMid meet"
        />
      )}
    </svg>
  )
}

export default function GuidedSizePresets({
  image,
  imageUrl,
  config,
  disabled = false,
  description = 'Choose a common size or enter custom dimensions below. One size is used for this design.',
  accessibleName,
  onSelect,
}) {
  const [previewPresetKey, setPreviewPresetKey] = useState(null)
  const tooltipIdPrefix = useId()
  const currentLongerSide = Math.max(
    Number(config?.printWidthIn) || 0,
    Number(config?.printHeightIn) || 0
  )
  const sourceName = accessibleName || image?.originalFilename || 'Artwork'

  return (
    <fieldset className="guided-size-presets" disabled={disabled}>
      <legend>Recommended garment sizes</legend>
      <p>{description}</p>
      <div className="guided-size-presets__options">
        {ARTWORK_SIZE_PRESETS.map(preset => {
          const selected = Math.abs(currentLongerSide - preset.longerSideIn) < 0.01
          const previewVisible = previewPresetKey === preset.key
          const tooltipId = `${tooltipIdPrefix}-${preset.key}`

          return (
            <span
              className="guided-size-presets__option"
              key={preset.key}
              onMouseEnter={() => setPreviewPresetKey(preset.key)}
              onMouseLeave={() => setPreviewPresetKey(null)}
              onBlur={event => {
                if (!event.currentTarget.contains(event.relatedTarget)) setPreviewPresetKey(null)
              }}
            >
              <button
                className={`guided-size-presets__button${selected ? ' guided-size-presets__button--selected' : ''}`}
                type="button"
                aria-pressed={selected}
                aria-label={`Use ${preset.label} size for ${sourceName}: ${preset.longerSideIn} inch longer side`}
                aria-describedby={previewVisible ? tooltipId : undefined}
                onFocus={() => setPreviewPresetKey(preset.key)}
                onClick={() => onSelect?.(preset)}
              >
                <span>{preset.label}</span>
                <small>{preset.longerSideIn}&quot;</small>
              </button>
              {previewVisible && (
                <span className="guided-size-presets__preview" id={tooltipId} role="tooltip">
                  <GarmentPreview preset={preset} imageUrl={imageUrl} />
                  <span className="guided-size-presets__preview-copy">
                    <strong>{preset.label} · {preset.longerSideIn}&quot; longer side</strong>
                    <span>{preset.description}</span>
                  </span>
                </span>
              )}
            </span>
          )
        })}
      </div>
    </fieldset>
  )
}
